import { t } from "@/i18n-js/instance";
import { useButtonVariant } from "@circle-react/helpers/useButtonVariant";
import { Button } from "@circle-react-uikit/Button";
import { Fieldset } from "@circle-react-uikit/Fieldset";
import { Form } from "@circle-react-uikit/Form";
import { useFilterContext } from "./FilterContext";

export interface RadioFilterOption {
  label: string;
  value: string;
}

export interface RadioFilterProps {
  legend: string;
  options: RadioFilterOption[];
  value: string;
}

/**
 * FilterV3-compatible RadioFilter component
 *
 * This component provides radio button filtering functionality that works
 * with the FilterV3 system. It should be used within a Filter component
 * from @circle-react-shared/FilterV3.
 *
 * @example
 * ```tsx
 * <Filter onApply={handleApply}>
 *   <Filter.Chip renderSelectedValue={() => selectedLabel}>
 *     Filter Name
 *   </Filter.Chip>
 *   <Filter.Popover title="Select Option">
 *     <RadioFilter
 *       options={[
 *         { label: "Option 1", value: "option1" },
 *         { label: "Option 2", value: "option2" }
 *       ]}
 *       legend="Choose an option"
 *       value={currentValue}
 *     />
 *   </Filter.Popover>
 * </Filter>
 * ```
 */
export const RadioFilter = ({ options, legend, value }: RadioFilterProps) => {
  const { onApply } = useFilterContext();
  const primaryButtonVariant = useButtonVariant();

  const handleApply = (submitData: { value: string }, { reset }: any) => {
    onApply({ value: submitData.value } as any);
    reset();
  };

  return (
    <div className="min-w-[18rem] p-4">
      <Form defaultValues={{ value }} onSubmit={handleApply}>
        <div className="flex flex-col gap-y-4">
          <Fieldset legend={legend} verticalSpacing="tight">
            <Form.Item
              name="value"
              hideLabel
              hideBorder
              hideDescription
              isFirstChild
            >
              <Form.RadioGroup name="value" variant="radio" options={options} />
            </Form.Item>
          </Fieldset>
          <Button variant={primaryButtonVariant} type="submit" full>
            {t("filters.apply")}
          </Button>
        </div>
      </Form>
    </div>
  );
};
